import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CmxClientService, getUserProfile, isInitialized, State } from '@cemex/cmx-client-layout';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, startWith, take, takeUntil } from 'rxjs/operators';
import { CustomCountlyService } from '@ordersnproducts/modules/countly/services/custom-countly.service';
import { CustomerLoadAction } from '@ordersnproducts/modules/shared/actions/customer.actions';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { environment } from 'environments/environment';
import { ordersAndTable } from '@ordersnproducts/modules/one-click-order/selectors/one-click-orders.selector';
import { OneClickModalComponent } from '@ordersnproducts/modules/one-click-order/containers/one-click-modal/one-click-modal.component';
import { OneClickTemplateBuilderComponent } from '@ordersnproducts/modules/one-click-order/containers/one-click-template-builder/one-click-template-builder.component';
import { OneClickOrderResetAction } from '@ordersnproducts/modules/one-click-order/actions/one-click-orders.actions';
import { Features } from '@ordersnproducts/features';
import { runJSQueue } from '@ordersnproducts/utils/run-js-queue';
import { OneClickLoadBusinessLinesAction } from '@ordersnproducts/modules/one-click-order/actions/one-click-business-lines.actions';
import { OneClickWarningModalComponent } from '@ordersnproducts/modules/one-click-order/containers/one-click-warning-modal/one-click-warning-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { OneClickOrdersLoadCompleteAction } from './../modules/one-click-order/actions/one-click-orders.actions';
import { Broadcaster } from '@cemex-core/angular-services-v8';
import { PRODUCT_LINE_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { ProductType } from '@cemex-core/cmx-api/models/business-lines/product-type';
import { CobrowseFeatureService } from 'app/services/cobrowse-feature.service';
import { EffectsListReducerState } from '@ordersnproducts/modules/shared/reducers/effects-list.reducer';
import { EffectsCreateListAction } from '@ordersnproducts/modules/shared/actions/effects-list.actions';
import { ProductLineCodesTypes } from '@cemex-core/cmx-api/models/business-lines/product-lines-codes';
import { getLocalStorageItem } from '../utils/safe-storage';
import { envVars } from 'environments/env-vars';

export enum QueryParams {
  OneClick = 'one-click',
  CalendarView = 'deliverysch',
}

@Component({
  selector: 'app-ordersnproducts-root',
  templateUrl: './ordersnproducts.component.html',
  styleUrls: ['./ordersnproducts.component.scss'],
})
export class OrdersnproductsComponent implements OnInit, OnDestroy {
  init: Observable<boolean>;
  private ngDestroy = new Subject();
  private walkMeInjected = false;
  private chatBotWizelineInjected = false;
  private chatBotAivoInjected = false;
  constructor(
    private store: Store<State>,
    private client: CmxClientService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private customCountlyService: CustomCountlyService,
    private featureToggle: FeatureToggleService,
    private translate: TranslateService,
    private broadcaster: Broadcaster,
    private cobrowseService: CobrowseFeatureService,
    private storeEffectsList: Store<EffectsListReducerState>,
  ) {}

  ngOnInit() {
    this.store.dispatch(new CustomerLoadAction());
    this.storeEffectsList.dispatch(new EffectsCreateListAction());
    this.init = this.store.pipe(select(isInitialized));
    this.client.hideGlobalLoader();
    this.store.pipe(select(getUserProfile), take(1)).subscribe((userProfile) => {
      this.customCountlyService.initialize(userProfile);
    });

    // Listen to params
    this.route.queryParams.pipe(takeUntil(this.ngDestroy)).subscribe((event) => {
      if (QueryParams.OneClick in event) {
        this.loadOneClickOrders(this.isOneClickParameter(event));
      }
    });

    if (this.featureToggle.onConfigurationFetched) {
      this.featureToggle.onConfigurationFetched.pipe(startWith({}), takeUntil(this.ngDestroy)).subscribe(() => {
        this.setCobrowseFeature();
      });
    }

    this.featureToggle.onConfigurationChanged.pipe(takeUntil(this.ngDestroy)).subscribe(() => {
      this.loadWalkMe();
      this.loadChatBotWizeline();
      this.loadChatBotAivo();
    });

    this.loadWalkMe();
    this.loadChatBotWizeline();
    this.loadChatBotAivo();
  }

  private isOneClickParameter(event: Params) {
    return Object.values(PRODUCT_LINE_CODE).find(
      (product) => product === event[QueryParams.OneClick] || product === event[QueryParams.OneClick][0],
    );
  }

  /**
   * Inject Walk Me scripts
   */
  private loadWalkMe() {
    const enabled = this.featureToggle.isFeatureEnabled(Features.WalkMe);
    if (!enabled || this.walkMeInjected || environment.name === 'mock') {
      return;
    }
    const walkme = document.createElement('script');
    walkme.type = 'text/javascript';
    walkme.async = true;
    const isProdHost = window.location.hostname.startsWith('www');
    const isQaHost = window.location.hostname.startsWith('qa');
    walkme.src = isProdHost ? envVars.WalkMeUrl.Prod : isQaHost ? envVars.WalkMeUrl.Qa : environment.walkMeUrl;
    const s = document.getElementsByTagName('script')[0];
    s.parentNode.insertBefore(walkme, s);
    window['_walkmeConfig'] = { smartLoad: true };
    window['walkme_get_language'] = function () {
      const lang = getLocalStorageItem('language') || sessionStorage.getItem('languague') || window['LANGUAGE'];
      return lang.replace('_', '-');
    };
    this.walkMeInjected = true;
  }

  private loadChatBotWizeline() {
    const enabled = this.featureToggle.isFeatureEnabled(Features.ChatBotWizeline);
    if (!enabled || this.chatBotWizelineInjected || environment.name === 'mock') {
      return;
    }

    const chatbotWizeline = document.createElement('script');
    chatbotWizeline.type = 'text/javascript';
    chatbotWizeline.async = true;
    chatbotWizeline.src = environment.chatBotWizelineUrl;
    const scriptTag = document.getElementsByTagName('script')[0];
    scriptTag.parentNode.insertBefore(chatbotWizeline, scriptTag);
    this.chatBotWizelineInjected = true;
  }

  private loadChatBotAivo() {
    const enabled = this.featureToggle.isFeatureEnabled(Features.ChatBotAivo);
    if (!enabled || this.chatBotAivoInjected || environment.name === 'mock') {
      return;
    }
    this.broadcaster.broadcast('Aivo_Chatbot', JSON.stringify('true'));
    // Mark as injected so the Aivo chatbot is initialized only once.
    // Without this guard, loadChatBotAivo() runs again on every onConfigurationChanged
    // emit and the Aivo widget re-attaches its shadow root, throwing
    // "NotSupportedError: ... attachShadow ... already hosts a shadow tree".
    this.chatBotAivoInjected = true;
  }

  /**
   * Loads one click orders
   */
  private loadOneClickOrders(productLineCode: ProductLineCodesTypes) {
    if (productLineCode) {
      this.client.showGlobalLoader();
      this.store.dispatch(new OneClickLoadBusinessLinesAction(productLineCode));
      this.initOneClickModal(productLineCode);
    }
  }

  /**
   * Inits one click modal.
   */
  private initOneClickModal(productLineCode: string) {
    this.store
      .pipe(
        select(ordersAndTable),
        filter(({ orders }) => !!orders),
        take(1),
      )
      .subscribe(({ canUseBusinessline, orders }) => {
        this.client.hideGlobalLoader();
        // ODM migration
        const filteredOrdersByBusinessLine = orders.filter(
          (order) =>
            order.orderProducts[0].productType.productTypeCode === (canUseBusinessline as ProductType).productTypeCode,
        );
        /* const filteredOrdersByBusinessLine = orders.filter(
          order =>
            order.items[0].product.productType.productTypeCode === (canUseBusinessline as ProductType).productTypeCode,
        ); */
        if (!canUseBusinessline) {
          const productLine = Object.keys(PRODUCT_LINE_CODE)
            .reduce((obj, key) => ((obj[PRODUCT_LINE_CODE[key]] = key), obj), {})
            [productLineCode].toLowerCase();
          this.openModal(
            OneClickWarningModalComponent,
            this.translate
              .instant('views.one_click.no_product_line_access')
              .replace('%p', this.translate.instant(`product_lines.${productLine}`)),
          );
          return;
        }
        if (filteredOrdersByBusinessLine.length > 0) {
          this.store.dispatch(new OneClickOrdersLoadCompleteAction(filteredOrdersByBusinessLine));
          this.initOneClickOrder();
        } else {
          this.initTemplateBuilder();
        }
      });
  }

  private initOneClickOrder() {
    runJSQueue(() => this.openModal(OneClickModalComponent));
  }

  private initTemplateBuilder() {
    this.client.hideGlobalLoader();
    runJSQueue(() => this.openModal(OneClickTemplateBuilderComponent));
    this.router.navigate([], { queryParams: { [QueryParams.OneClick]: null }, queryParamsHandling: 'merge' });
  }

  private openModal(component, message = '') {
    const modalRef = this.dialog.open(component, {
      width: '800px',
      maxWidth: '100%',
      data: { message },
    });

    this.handleModalClose(modalRef);
  }

  private setCobrowseFeature() {
    const enabled = this.featureToggle.isFeatureEnabled(Features.CoBrowse);
    this.cobrowseService.setCobrowseFeatureValue(enabled);
  }

  /**
   * Handle when clicking outside or closing the modal
   * @param dialog Ref
   */
  private handleModalClose(dialog: MatDialogRef<{}, any>) {
    dialog
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.store.dispatch(new OneClickOrderResetAction()));
  }

  public ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
